import React from 'react';
import Footer from './Footer';
import { isAppAccessBlocked } from '../src/utils/launchGate';

export const LandingPage: React.FC = () => {
  const appBlocked = isAppAccessBlocked();

  return (
    <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col overflow-x-hidden selection:bg-cv-interactive/40 selection:text-cv-primary">
      {/* Background gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[600px] bg-[radial-gradient(ellipse_at_top,rgba(180,154,99,0.12),transparent_50%)] pointer-events-none z-0" />
      <div className="absolute top-[40%] right-0 w-[400px] h-[400px] bg-cv-gold/5 blur-[120px] rounded-full pointer-events-none z-0" />

      {/* Header */}
      <header className="max-w-6xl w-full mx-auto px-6 py-6 flex justify-between items-center z-10 relative">
        <div className="flex items-center gap-3">
          <span className="text-2xl font-black text-cv-gold tracking-tighter">Chronovela</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/help" className="text-xs font-bold text-cv-muted hover:text-cv-primary transition-colors">Help</a>
          {appBlocked ? (
            <button
              type="button"
              disabled
              aria-disabled="true"
              title="Coming soon"
              className="px-5 py-2 bg-cv-interactive/60 text-cv-page rounded-lg font-bold text-xs uppercase tracking-wider shadow-lg shadow-black/30 cursor-not-allowed opacity-80"
            >
              Coming Soon
            </button>
          ) : (
            <>
              <a href="/app" className="text-xs font-bold text-cv-muted hover:text-cv-primary transition-colors">Sign In</a>
              <a
                href="/app"
                className="px-5 py-2 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded-lg font-bold text-xs uppercase tracking-wider transition-all shadow-lg shadow-black/30 transform hover:-translate-y-0.5"
              >
                Start Free Preview
              </a>
            </>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col z-10 relative max-w-6xl w-full mx-auto px-6 pt-12 pb-24">
        <section className="text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-5xl md:text-6xl font-black text-cv-primary tracking-tight leading-none mb-6">
            Write full-length novels with
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cv-gold via-cv-gold-soft to-cv-interactive mt-2">
              continuity built in.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-cv-muted font-medium max-w-2xl mx-auto mb-10 leading-relaxed">
            Chronovela helps authors create structured long-form manuscripts by combining story blueprints, editable chapter roadmaps, guided generation, story memory, rewrite passes, and manuscript export.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            {appBlocked ? (
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Coming soon"
                className="w-full sm:w-auto px-8 py-4 bg-cv-interactive/60 text-cv-page rounded-xl font-bold text-sm uppercase tracking-wider text-center shadow-xl shadow-black/40 cursor-not-allowed opacity-80"
              >
                Coming Soon
              </button>
            ) : (
              <a
                href="/app"
                className="w-full sm:w-auto px-8 py-4 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded-xl font-bold text-sm uppercase tracking-wider transition-all shadow-xl shadow-black/40 transform hover:-translate-y-0.5 text-center"
              >
                Start Free Preview
              </a>
            )}
            <a
              href="#how-it-works"
              className="w-full sm:w-auto px-8 py-4 bg-cv-panel hover:bg-cv-elevated text-cv-secondary rounded-xl font-bold text-sm uppercase tracking-wider transition-all border border-cv-border text-center"
            >
              How It Works
            </a>
          </div>
        </section>

        {/* Process Cards Section */}
        <section id="how-it-works" className="mb-24 scroll-mt-6">
          <h2 className="text-xs font-mono font-black text-cv-gold uppercase tracking-widest text-center mb-12">
            The Chronovela Writing Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-cv-panel/40 border border-cv-border p-8 rounded-2xl relative overflow-hidden group hover:border-cv-border-strong transition-all">
              <div className="w-12 h-12 bg-cv-gold/10 border border-cv-gold/20 rounded-xl flex items-center justify-center mb-6">
                <span className="text-lg">🧭</span>
              </div>
              <h3 className="text-lg font-bold text-cv-primary mb-3 tracking-tight">Build Your Story Blueprint</h3>
              <p className="text-sm text-cv-muted leading-relaxed">
                Define your characters, world rules, genre, tone, and hard canon before the book starts.
              </p>
            </div>

            <div className="bg-cv-panel/40 border border-cv-border p-8 rounded-2xl relative overflow-hidden group hover:border-cv-border-strong transition-all">
              <div className="w-12 h-12 bg-cv-gold/10 border border-cv-gold/20 rounded-xl flex items-center justify-center mb-6">
                <span className="text-lg">🗺️</span>
              </div>
              <h3 className="text-lg font-bold text-cv-primary mb-3 tracking-tight">Approve the Chapter Roadmap</h3>
              <p className="text-sm text-cv-muted leading-relaxed">
                Review and edit a chapter-by-chapter plan so the manuscript follows the direction you want.
              </p>
            </div>

            <div className="bg-cv-panel/40 border border-cv-border p-8 rounded-2xl relative overflow-hidden group hover:border-cv-border-strong transition-all">
              <div className="w-12 h-12 bg-cv-success/10 border border-cv-success/20 rounded-xl flex items-center justify-center mb-6">
                <span className="text-lg">💾</span>
              </div>
              <h3 className="text-lg font-bold text-cv-primary mb-3 tracking-tight">Generate with Story Memory</h3>
              <p className="text-sm text-cv-muted leading-relaxed">
                Write chapters while Chronovela tracks important events, character states, and continuity.
              </p>
            </div>
          </div>
        </section>

        {/* Book Credits Section */}
        <section className="bg-cv-panel/25 border border-cv-border rounded-3xl p-8 md:p-12 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[10px] font-mono font-black text-cv-interactive uppercase tracking-widest block mb-3">
                Pricing
              </span>
              <h2 className="text-3xl font-black text-cv-primary uppercase tracking-tight mb-4">
                Choose the right volume size
              </h2>
              <p className="text-cv-muted text-sm leading-relaxed mb-6">
                Chronovela uses Book Credits instead of subscriptions. Pick the volume size that matches your manuscript.
              </p>
              <ul className="space-y-3 text-sm text-cv-secondary">
                <li className="flex items-center gap-3">
                  <span className="text-cv-success">✓</span> <strong>Standard Manuscript Credit</strong> — up to 20 chapters, 5 included rewrites
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-cv-success">✓</span> <strong>Large Manuscript Credit</strong> — up to 30 chapters, 8 included rewrites
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-cv-success">✓</span> <strong>Epic Manuscript Credit</strong> — up to 40 chapters, 10 included rewrites
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-cv-success">✓</span> <strong>Rewrite Pack</strong> — extra rewrites and polish attempts
                </li>
              </ul>
              <div className="mt-8 text-xs font-mono text-cv-muted uppercase tracking-wider">
                🔒 Chronovela is currently in beta testing. Billing and Book Credit purchasing are being prepared.
              </div>
            </div>
            <div className="bg-cv-page/60 border border-cv-border/80 p-8 rounded-2xl">
              <h3 className="text-sm font-bold text-cv-primary mb-4 uppercase tracking-wider font-mono">
                Advanced Features
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-bold text-cv-gold uppercase">Guided Writing Workspace</h4>
                  <p className="text-xs text-cv-muted mt-1">
                    Add notes, adjust direction, and guide the manuscript as chapters are written.
                  </p>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-cv-gold uppercase">Export Formats</h4>
                  <p className="text-xs text-cv-muted mt-1">
                    Export completed manuscripts as DOCX or text files.
                  </p>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-cv-gold uppercase">No API Setup</h4>
                  <p className="text-xs text-cv-muted mt-1">
                    Start writing without managing model keys or technical setup.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Responsibility Note & Company Verification */}
        <section className="text-center max-w-2xl mx-auto mb-12 pt-12 border-t border-cv-border">
          <p className="text-xs text-cv-muted leading-relaxed font-medium mb-12">
            Chronovela is a writing tool. Authors should review, edit, and verify generated manuscripts before publishing.
          </p>

          <div className="bg-cv-panel/30 border border-cv-border p-8 rounded-2xl text-left">
            <h4 className="text-sm font-bold text-cv-primary uppercase tracking-widest mb-4">Company Verification</h4>
            <p className="text-sm text-cv-muted mb-6 leading-relaxed">
              Chronovela is developed and operated by Skald Haven Inc., a Florida-based software company building AI-assisted writing systems for authors.
            </p>
            <ul className="text-sm text-cv-muted space-y-3 font-mono">
              <li><strong className="text-cv-secondary">Product:</strong> Chronovela</li>
              <li><strong className="text-cv-secondary">Company:</strong> Skald Haven Inc.</li>
              <li><strong className="text-cv-secondary">Founder:</strong> Brian Hesters</li>
              <li><strong className="text-cv-secondary">Location:</strong> Florida, United States</li>
              <li><strong className="text-cv-secondary">Website:</strong> <a href="https://chronovela.app" className="text-cv-gold hover:text-cv-interactive transition-colors">chronovela.app</a></li>
              <li><strong className="text-cv-secondary">Contact:</strong> <a href="mailto:SkaldHaven@gmail.com" className="text-cv-gold hover:text-cv-interactive transition-colors">SkaldHaven@gmail.com</a></li>
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
